import { useVerificationFlow } from '../../../Components/shared/VerificationFlow'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch } from '../../../store'
import { verifyTeamMembership } from '../../../store/slices/homebotNetwork/clientTeamMemberships'
import { useState } from 'react'
import * as Sentry from '@sentry/browser'
import { Link, useToast } from '@homebotapp/hb-react-component-catalog'
import { PATH_HELPERS } from '../../../constants/navigation'
import { defineMessages, useIntl } from 'react-intl'
import { useGetClientId } from '../../../hooks/useGetClientId'
import { formatCustomerProfileType } from '../../../helpers/formatCustomerProfileType'
import { selectCustomerId } from '../../../store/selectors/customerProfile'

const MSG = defineMessages({
  successToastTitle: {
    id: 'AddPreferredAgentForm.successToastTitle',
    defaultMessage: '{partnerType} Added'
  },
  successToastDetails: {
    id: 'AddPreferredAgentForm.successToastDetails',
    defaultMessage: "They've been notified, and you can {linkText}."
  },
  successToastLinkText: {
    id: 'AddPreferredAgentForm.successToastLinkText',
    defaultMessage: 'update or remove them here'
  }
})

export const useUnverifiedStep = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { state, dispatch: dispatchVerificationFlow } = useVerificationFlow()
  const { currentPartnerType } = state
  const [isLoading, setIsLoading] = useState(false)
  const toast = useToast()
  const intl = useIntl()
  const clientId = useGetClientId()
  const customerProfileId = useSelector(selectCustomerId)
  const teamUrl = PATH_HELPERS.team.buildPath(customerProfileId, clientId)
  const linkText = intl.formatMessage(MSG.successToastLinkText)
  const getMembership = () => {
    switch (currentPartnerType) {
      case 'RealEstateAgent':
        return state.realEstateAgentMembership
      case 'LoanOfficer':
        return state.loanOfficerMembership
      case 'InsuranceAgent':
        return state.insuranceAgentMembership
      default:
        return state.loanOfficerMembership
    }
  }
  const membership = getMembership()

  const onRemove = () => {
    dispatchVerificationFlow({ type: 'REMOVE_PARTNER' })
  }

  const onVerify = async () => {
    setIsLoading(true)
    try {
      // First, verify the team membership
      const action = await dispatch(
        verifyTeamMembership({
          clientId: membership.clientId,
          clientTeamMembershipId: membership.id
        })
      )

      if (action.meta.requestStatus === 'fulfilled') {
        toast({
          title: intl.formatMessage(MSG.successToastTitle, {
            partnerType: formatCustomerProfileType(currentPartnerType)
          }),
          description: intl.formatMessage(MSG.successToastDetails, {
            linkText: (
              <Link href={teamUrl} color='primary.500' textDecoration='underline' _hover={{ color: 'primary.600' }}>
                {linkText}
              </Link>
            )
          }),
          status: 'success',
          duration: 7000
        })
        // For any partner type, just add the partner (goes to messaging)
        dispatchVerificationFlow({ type: 'ADD_PARTNER' })
      }
    } catch (error) {
      Sentry.captureException(error)
    } finally {
      setIsLoading(false)
    }
  }

  return {
    membership,
    onRemove,
    onVerify,
    isLoading
  }
}
